import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Definición de paletas de colores
export interface ColorPalette {
  id: string;
  name: string;
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

// Paleta PEEK PASS - negro, verde y superficies claras.
export const colorPalettes: ColorPalette[] = [
  {
    id: 'peekpass',
    name: 'PEEK PASS',
    primary: '#b8ef4f',
    secondary: '#050505',
    accent: '#86c92b',
    background: '#f8f7f1',
    surface: '#ffffff',
    text: '#101010',
    textSecondary: '#59615b',
    border: '#dce4d4',
    success: '#2f7d32',
    warning: '#b97900',
    error: '#c63131',
    info: '#347a60',
  }
];

interface ColorContextType {
  currentPalette: ColorPalette;
  setCurrentPalette: (palette: ColorPalette) => void;
  palettes: ColorPalette[];
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

interface ColorProviderProps {
  children: ReactNode;
}

export const ColorProvider: React.FC<ColorProviderProps> = ({ children }) => {
  const [currentPalette, setCurrentPaletteState] = useState<ColorPalette>(colorPalettes[0]);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Cargar paleta guardada del localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';

    setCurrentPaletteState(colorPalettes[0]);
    localStorage.setItem('colorPalette', 'peekpass');
    
    setIsDarkMode(savedDarkMode);
  }, []);

  // Aplicar paleta al CSS
  useEffect(() => {
    const root = document.documentElement;
    const palette = currentPalette;

    // Aplicar variables CSS
    root.style.setProperty('--color-primary', palette.primary);
    root.style.setProperty('--color-secondary', palette.secondary);
    root.style.setProperty('--color-accent', palette.accent);
    root.style.setProperty('--color-background', palette.background);
    root.style.setProperty('--color-surface', palette.surface);
    root.style.setProperty('--color-text', palette.text);
    root.style.setProperty('--color-text-secondary', palette.textSecondary);
    root.style.setProperty('--color-border', palette.border);
    root.style.setProperty('--color-success', palette.success);
    root.style.setProperty('--color-warning', palette.warning);
    root.style.setProperty('--color-error', palette.error);
    root.style.setProperty('--color-info', palette.info);

    // Aplicar modo oscuro
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Aplicar estilos específicos para Tailwind CSS
    root.style.setProperty('--tw-color-primary', palette.primary);
    root.style.setProperty('--tw-color-secondary', palette.secondary);
    root.style.setProperty('--tw-color-accent', palette.accent);
  }, [currentPalette, isDarkMode]);

  const setCurrentPalette = (palette: ColorPalette) => {
    setCurrentPaletteState(palette);
    localStorage.setItem('colorPalette', palette.id);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
  };

  return (
    <ColorContext.Provider
      value={{
        currentPalette,
        setCurrentPalette,
        palettes: colorPalettes,
        isDarkMode,
        toggleDarkMode,
      }}
    >
      {children}
    </ColorContext.Provider>
  );
};

export const useColors = (): ColorContextType => {
  const context = useContext(ColorContext);
  if (context === undefined) {
    throw new Error('useColors must be used within a ColorProvider');
  }
  return context;
};
